// sectionUtils.js — build sections first, then set location.quantity after promo

// ---------- helpers ----------
function piecesForItem(item) {
  let q = 0;
  (item?.order_item_colors || []).forEach((color) => {
    (color.sizes || []).forEach((s) => (q += Number(s.quantity || 0)));
    (color.inner_colors || []).forEach((ic) =>
      (ic.sizes || []).forEach((s) => (q += Number(s.quantity || 0)))
    );
  });
  return q;
}

function normClass(item) {
  return String(
    item?.style_info?.product_class ||            // ← primary
    item?.style_info?.product_class_code ||       // common alt
    item?.program_info?.product_class ||          // fallback
    ''
  ).trim().toLowerCase();
}

function promoIsActive(promo) {
  if (!promo) return false;
  const type = String(promo.discount_type || "").toLowerCase();
  const applies =
    promo.applies_to_current_order === true ||
    promo.applies_to_current_order === 1 ||
    promo.applies_to_current_order === "1";

  return (
    (type === "free_locations" || Number(promo.id) === 7) &&
    Number(promo.disabled) !== 1 &&
    applies
  );
}

function promoClassSet(promo) {
  if (!promo) return null; // null => no list provided => treat as "all classes eligible"
  let list =
    promo.applies_to_product_classes ??
    promo.product_classes ??
    promo.allowed_classes ??
    null;
  if (list == null) return null;
  if (!Array.isArray(list)) list = String(list).split(/[,\s]+/).filter(Boolean);
  const norm = (s) => String(s || "").trim().toLowerCase();
  const set = new Set(list.map(norm));
  return set.size ? set : null; // null => all eligible
}

// Public: used by pdf_view and here to match item location rows
export function generateLocationKey(location) {
  // Reproduce the original "filePart" + optional "_text" behavior exactly
  let filePart = '';

  if (location?.decoration) {
    filePart = `${location.location.location_code}_${location.decoration.id}`;
  } else if (location?.flow_image) {
    const fn = String(location.flow_image.file_name || '').replace(/[ ,.-]/g, '_');
    filePart = `${location.location.location_code}_${fn}`;
  } else if (location?.new_decoration_data) {
    const nm = String(location.new_decoration_data.name || '').replace(/[ ,.-]/g, '_');
    filePart = `${location.location.location_code}_${nm}`;
  }
  // NOTE: if it's text-only, filePart intentionally stays '' (legacy behavior)

  let key = filePart;

  if (location?.decoration_text) {
    const cleanText = String(location.decoration_text).replace(/[ ,.-]/g, '_');
    key += `_${cleanText}`;   // yields "_text" for text-only, matching your sections
  }

  return key;
}

// ---------- main ----------
export async function configureSections(
  orderItems,
  customerInfo = {},
  activePromotion = null
) {
  const obj = {};
  for (const val of orderItems || []) {
    // --- build a unique section key like your original code did ---
    let name = "";
    if (val.program_info) {
      name = val.program_info.program_name.replace(/[ ()]/g, "_");
    }

    const arr = []; // flat copy of original locs
    const loc = {}; // temp per-item loc accumulator

    if (!val.blank_order) {
      for (const location of val.order_item_locations || []) {
        if (!location || !location.location || !location.technique) {
          continue;
        }
        const techniqueName = location.technique?.technique_name || "";
        name += `_${techniqueName}`;

        let filePart = "";
        var fileName = "";
        if (location.decoration) {
          filePart = `${location.location.location_code}_${location.decoration.id}`;
          fileName = location.decoration.name
        } else if (location.flow_image) {
          filePart = `${location.location.location_code}_${location.flow_image.file_name.replace(
            /[ ,.-]/g,
            "_"
          )}`;
          fileName = location.flow_image.file_name
        } else if (location.new_decoration_data) {
          filePart = `${location.location.location_code}_${location.new_decoration_data.name.replace(
            /[ ,.-]/g,
            "_"
          )}`;
          fileName = location.new_decoration_data.name
        }

        name += `_${filePart}`;
        arr.push(location);

        let locName = generateLocationKey(location);
        if (location.decoration_text) {
          const cleanText = location.decoration_text.replace(/[ ,.-]/g, "_");
          name += `_${cleanText}`;
        }

        if (!loc[locName]) {
          loc[locName] = {
            location_id: location.location.location_id,
            location_name: location.location.location_name,
            location_code: location.location.location_code,
            layout_text: location.layout_text,
            key_name: locName,
            technique: location.technique,
            file_name: fileName,
            color_type: location.color_type,
            cost: location.cost,
            combined: location.location.combined || false,
            // keep CSR dollars intact
            technique_cost: parseFloat(location.technique_cost) || 0,
            one_to_23_upcharge_cost: location.one_to_23_upcharge_cost || 0,
            show_one_to_23: false,
            blank_order: false,
            // we will fill this with total pieces first, then overwrite after promo
            quantity: 0,
          };
        }
      }
    } else {
      name += "_BLANK";
      loc[name] = {
        location_name: "BLANK",
        location_code: "BLANK",
        layout_text: "",
        file_name: "BLANK",
        technique: "BLANK",
        color_type: "BLANK",
        key_name: 'BLANK',
        combined: false,
        cost: 0,
        technique_cost: 0,
        one_to_23_upcharge_cost: 0,
        show_one_to_23: false,
        blank_order: true,
        quantity: 0,
      };
    }

    // === Add up all color/size quantities once per item, and give that to every location ===
    const itemPieces = piecesForItem(val);
    for (const key in loc) {
      loc[key].quantity += itemPieces;
    }

    // === Ensure section bucket exists ===
    if (!obj[name]) {
      obj[name] = {
        order_items: [],
        order_item_locations: arr, // flat copy for images/etc.
        locations_object: {}, // key => merged row
        additional_locations_charge: 0,
        additional_locations_charge_cost: "",
        additional_locations_charge_quantity: 0,
        _authoritative_additional_quantity: 0,
        _authoritative_additional_extension: 0,
        section_name: name
      };
    }

    // === Push location rows into section ===
    let locIndex = 1;
    for (const key in loc) {
      if (!obj[name].locations_object[key]) {
        obj[name].locations_object[key] = {
          location_id: loc[key].location_id,
          location_name: loc[key].location_name,
          location_code: loc[key].location_code,
          layout_text: loc[key].layout_text,
          file_name: loc[key].file_name,
          key_name: loc[key].key_name,
          technique: loc[key].technique,
          color_type: loc[key].color_type,
          section_name: name,
          cost: loc[key].cost,
          technique_cost: parseFloat(loc[key].technique_cost) || 0,
          one_to_23_upcharge_cost: loc[key].one_to_23_upcharge_cost || 0,
          one_to_23_upcharge_price: 0,
          show_one_to_23: loc[key].show_one_to_23,
          blank_order: loc[key].blank_order,
          quantity: loc[key].quantity, // <-- will be overwritten after promo finalize
          additional_locations_charge_quantity: 0,
        };
      } else {
        obj[name].locations_object[key].quantity += parseInt(loc[key].quantity);
      }
      console.log('section utils')
      console.log(loc[key])
      if (loc[key].combined) {
        locIndex++
      }
      // Keep your original additional locations count behavior
      if (
        loc[key].technique &&
        locIndex > 1
      ) {
        obj[name].locations_object[key].additional_locations_charge_quantity =
          loc[key].quantity;
      }

      locIndex++;
    }

    obj[name].order_items.push(val);

    // Pass-through program-level additional locations charge
    if (val.program_info?.additional_locations_charge) {
      obj[name].additional_locations_charge = 1;
      obj[name].additional_locations_charge_cost =
        parseFloat(val.additional_locations_charge_cost) || 0;
      // obj[name].additional_locations_charge_quantity += val.units || 0;
      console.log('Adding additional locations charge quantity from program_info:');
      console.log(obj[name])
      console.log(val)
      for (const k in obj[name].locations_object) {
        obj[name].additional_locations_charge_quantity +=
          obj[name].locations_object[k].additional_locations_charge_quantity;
      }
    }
    if (val.authoritative_additional_locations) {
      const authoritative = val.authoritative_additional_locations;
      obj[name].additional_locations_charge = Number(authoritative.enabled || 0);
      obj[name]._authoritative_additional_quantity += Number(authoritative.quantity || 0);
      obj[name]._authoritative_additional_extension += Number(authoritative.extension || 0);
      obj[name].additional_locations_charge_quantity = obj[name]._authoritative_additional_quantity;
      obj[name].additional_locations_charge_cost = obj[name]._authoritative_additional_quantity > 0
        ? obj[name]._authoritative_additional_extension / obj[name]._authoritative_additional_quantity
        : 0;
    }
  }

  // === FINAL PASS: overwrite location.quantity based on the promo (ONLY quantity!) ===
  const promoOn = promoIsActive(activePromotion);
  const classSet = promoClassSet(activePromotion); // null => all classes eligible
  const minQty = Number(activePromotion?.min_quantity_required || 0);

  if (promoOn) {
    for (const secKey in obj) {
      const section = obj[secKey];

      // Section qualifies if eligible class pieces meet min
      let eligiblePieces = 0;
      for (const item of section.order_items || []) {
        const eligible =
          classSet === null ? true : classSet.has(normClass(item));
        if (eligible) eligiblePieces += piecesForItem(item);
      }
      const qualifies = eligiblePieces >= minQty;

      if (Number(activePromotion?.id) === 7) {
        console.log('chatgpt log', {
          promoId: activePromotion.id,
          classSet,
          eligiblePieces,
          minQty,
          qualifies,
          productClass: normClass(section.order_items[0])
        });
      }

      if (!qualifies) continue;

      // For each location row, compute billed qty:
      // - no class list -> 0
      // - with class list -> sum pieces of items NOT in classes that actually have that location
      for (const locKey in section.locations_object || {}) {
        const row = section.locations_object[locKey];

        if (Number(activePromotion?.id) === 7) {
          const heatsealTechniqueIds = ["104", "116", "121", "137", "175", "176", "178", "181", "182"];
          const rowLocationCode = String(row.location_code || "");
          const rowTechniqueId = String(row.technique?.technique_id || "");

          if (
            rowLocationCode === "CF1" &&
            heatsealTechniqueIds.includes(rowTechniqueId)
          ) {
            row.cost = 0;
            row.technique_cost = 0;
          }

          continue;
        }

        if (classSet === null) {
          row.quantity = 0; // all classes eligible → all free
          continue;
        }

        let billed = 0;
        for (const item of section.order_items || []) {
          const hasThisLoc = (item.order_item_locations || []).some(
            (L) => generateLocationKey(L) === locKey
          );
          if (!hasThisLoc) continue;

          const inPromo = classSet.has(normClass(item));
          if (!inPromo) billed += piecesForItem(item);
        }

        row.quantity = billed; // ← THE ONLY THING WE CHANGE
      }
    }
  }

  return obj;
}
